import { CircleAlert, CircleCheck, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Portal } from '../../internal/Portal.tsx';
import { useToastStore, type ToastItem } from './toast-store.ts';
import styles from './Toast.module.css';

function Toast({ item }: { item: ToastItem }) {
  const dismiss = useToastStore(state => state.dismiss);
  const [paused, setPaused] = useState(false);
  const left = useRef(item.durationMs);
  useEffect(() => {
    if (paused) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => dismiss(item.id), left.current);
    return () => {
      clearTimeout(timer);
      left.current -= Date.now() - startedAt;
    };
  }, [paused, dismiss, item.id]);
  return (
    <li
      className={styles.toast}
      data-tone={item.tone}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {item.tone === 'ok' ? <CircleCheck className={styles.icon} aria-hidden="true" /> : null}
      {item.tone === 'error' ? <CircleAlert className={styles.icon} aria-hidden="true" /> : null}
      <span className={styles.message}>{item.message}</span>
      {item.action ? (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            item.action!.onClick();
            dismiss(item.id);
          }}
        >
          {item.action.label}
        </button>
      ) : null}
      <button type="button" className={styles.close} aria-label="Cerrar el aviso" onClick={() => dismiss(item.id)}>
        <X size={18} aria-hidden="true" />
      </button>
    </li>
  );
}

/** Mount once near the root; shows the notices pushed with `toast()`. */
export function Toaster() {
  const items = useToastStore(state => state.items);
  return (
    <Portal>
      <section className={styles.region} aria-label="Avisos">
        <ol className={styles.list} aria-live="polite" aria-relevant="additions">
          {items.map(item => (
            <Toast key={item.id} item={item} />
          ))}
        </ol>
      </section>
    </Portal>
  );
}
